import { Container, Heading, Stack, Spacer, Box, Text, useBreakpointValue } from "@chakra-ui/react";
import { useColorMode, useColorModeValue } from "../../../components/ui/color-mode";
import { FeatureListItem } from "./Features";
import { FaGithub } from "react-icons/fa6";
import { BiCollection, BiWrench } from "react-icons/bi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/bundle";
import "swiper/css/navigation";
import "swiper/css/pagination";

const featureList = [
    {
        heading: "Collect data in one place",
        icon: BiCollection,
        text: "Control, manage, and analyze your bioprocess from anywhere in the world. Live sensor data, process history, and analytics are always at your fingertips — no more exporting spreadsheets by hand.",
    },
    {
        heading: "Build and repair by yourself",
        icon: BiWrench,
        text: "Every schematic, every line of code is yours. Build your own sensors with our open hardware guides, extend the REST API, or adapt the dashboard to fit your exact workflow.",
    },
    {
        heading: "Open standard for everyone",
        icon: FaGithub,
        text: "LabChron speaks open protocols so any device, any team, any university can plug in. Push data through our public API, pull it into your analysis tools, and share results freely.",
    },
];

function InfoPage() {
    const { colorMode } = useColorMode();
    const pageColor = colorMode === "dark" ? "white" : "blue";
    const subtitleColor = useColorModeValue("gray.600", "gray.400");

    const isMobile = useBreakpointValue({ base: true, md: false });

    return (
        <Container maxW={{ base: "95%", sm: "90%", xl: "60%" }} px={{ base: 2, md: 4 }} align="center">
            <Stack gap={{ base: "4", md: "5" }} align="center">
                <Heading as="h1" size="xl" textAlign="center">
                    Science with LabChron
                </Heading>
                <Text width={{ base: "100%", xl: "80%" }} textAlign="center" fontSize={{ base: "md", md: "lg" }} color={subtitleColor} mb={5} lineHeight="1.7">
                    LabChron provides a flexible sensor platform for the monitoring and control of scientific experiments, research projects, and
                    their data. Our platform is designed to streamline the management of research and promote open standards in the laboratory of the
                    future. Join us in our mission to <Text as="span" fontWeight="bold">#UnlockOpenScience</Text>
                </Text>
                <Spacer />

                <Box w="100%">
                    {/* Mobile view */}
                    {isMobile && (
                        <Swiper
                            direction={"horizontal"}
                            modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
                            spaceBetween={10}
                            slidesPerView={1}
                            navigation={false}
                            loop={true}
                            keyboard={true}
                            pagination={{
                                clickable: true,
                                type: "bullets",
                                dynamicBullets: true,
                                dynamicMainBullets: 3,
                            }}
                            style={{
                                "--swiper-pagination-color": pageColor,
                                "--swiper-pagination-bullet-inactive-color": pageColor,
                            }}
                            autoplay={{
                                delay: 10000,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }}
                        >
                            {featureList.map((feature, index) => (
                                <SwiperSlide key={index}>
                                    <FeatureListItem
                                        key={index}
                                        icon={feature.icon}
                                        heading={feature.heading}
                                        text={feature.text}
                                        isReversed={false}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}
                    {/* Desktop view */}
                    {!isMobile && (
                        <Stack direction="column" gap={{ base: "0"}} align="center" justify="center">
                            {featureList.map((feature, index) => (
                                <FeatureListItem
                                    key={index}
                                    icon={feature.icon}
                                    heading={feature.heading}
                                    text={feature.text}
                                    isReversed={index % 2 === 0}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>

                <Spacer />

                {/*
                    <CounterComponent number={counterValue} mb={5} />;
                
                    <Grid
                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                        gap={5}
                        justifyContent={{ md: "center", lg: "start" }}
                        alignItems="center"
                    >
                        <CustomInfoButton icon={PiTerminalWindow} buttonText="Try LabChron Console" buttonHeight="80px" />
                        <CustomInfoButton icon={PiCodesandboxLogo} buttonText="Get a Sensor Kit" buttonHeight="80px" />
                        <GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
                            <CustomInfoButton icon={FaUsers} buttonText="Join the community" buttonHeight="80px" />
                        </GridItem>
                    </Grid>
                */}
            </Stack>
        </Container>
    );
}

export default InfoPage;
